import React, {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  useSidebarPinState,
} from '@backstage/core-components';
import {
  alertApiRef,
  appThemeApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import {
  UNSAFE_NavigationContext,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useObservable } from 'react-use';
import { ServiceRegistryLocale } from '@internal/service-registry-i18n';
import {
  ServiceRegistryProfileInput,
  useServiceRegistryLanguage,
  useServiceRegistryTranslation,
} from '../../i18n/ServiceRegistryLanguageProvider';

type FieldErrors = Partial<
  Record<
    | 'contactPhone'
    | 'jobTitle'
    | 'managerName'
    | 'managerEmail'
    | 'managerPhone'
    | 'managerTitle',
    string
  >
>;

const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const phonePattern = /^(\+\d{1,4}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const useStyles = makeStyles(theme => ({
  fieldset: {
    border: 0,
    margin: 0,
    padding: 0,
    width: '100%',
  },
  appearanceItem: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingLeft: 0,
    paddingRight: 0,
  },
  appearanceRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    paddingLeft: 0,
    paddingRight: 0,
  },
  appearanceText: {
    margin: 0,
    minWidth: 0,
  },
  themeButtons: {
    maxWidth: '100%',
    '& .MuiButton-root': {
      minWidth: 64,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      whiteSpace: 'nowrap',
    },
  },
  actions: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    bottom: 0,
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    minHeight: '4rem',
    padding: theme.spacing(1, 2),
    position: 'sticky',
    zIndex: theme.zIndex.appBar,
  },
  submitWrapper: {
    position: 'relative',
  },
  buttonProgress: {
    left: '50%',
    marginLeft: -12,
    marginTop: -12,
    position: 'absolute',
    top: '50%',
  },
}));

const emptyInput = (
  locale: ServiceRegistryLocale,
): ServiceRegistryProfileInput => ({
  contactPhone: '',
  jobTitle: '',
  department: '',
  manager: { name: '', email: '', phone: '', title: '' },
  preferredLocale: locale,
});

type AppearanceCardProps = {
  preferredLocale: ServiceRegistryLocale;
  onLocaleChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

const AppearanceCard = ({
  preferredLocale,
  onLocaleChange,
}: AppearanceCardProps) => {
  const { t } = useServiceRegistryTranslation();
  const classes = useStyles();
  const appThemeApi = useApi(appThemeApiRef);
  const { isPinned, toggleSidebarPinState, isMobile } = useSidebarPinState();
  const themeId = useObservable(
    appThemeApi.activeThemeId$(),
    appThemeApi.getActiveThemeId(),
  );
  const themes = appThemeApi.getInstalledThemes();

  const labelForTheme = (id: string, title: string) => {
    if (id.toLowerCase().includes('light')) return t('settings.light');
    if (id.toLowerCase().includes('dark')) return t('settings.dark');
    return title;
  };

  return (
    <InfoCard title={t('settings.appearance')} variant="gridItem">
      <List dense>
        <ListItem className={classes.appearanceItem}>
          <ListItemText
            className={classes.appearanceText}
            primary={t('settings.theme')}
            secondary={t('settings.themeHelp')}
          />
          <ButtonGroup
            className={classes.themeButtons}
            size="small"
            aria-label={t('settings.theme')}
          >
            {themes.map(theme => (
              <Button
                key={theme.id}
                color={themeId === theme.id ? 'primary' : 'default'}
                variant={themeId === theme.id ? 'contained' : 'outlined'}
                onClick={() => appThemeApi.setActiveThemeId(theme.id)}
              >
                {labelForTheme(theme.id, theme.title)}
              </Button>
            ))}
            <Button
              color={themeId === undefined ? 'primary' : 'default'}
              variant={themeId === undefined ? 'contained' : 'outlined'}
              onClick={() => appThemeApi.setActiveThemeId(undefined)}
            >
              {t('settings.auto')}
            </Button>
          </ButtonGroup>
        </ListItem>
        {!isMobile && (
          <ListItem className={classes.appearanceRow}>
            <ListItemText
              className={classes.appearanceText}
              primary={t('settings.pinSidebar')}
              secondary={t('settings.pinSidebarHelp')}
            />
            <Switch
              color="primary"
              checked={isPinned}
              onChange={() => toggleSidebarPinState()}
              inputProps={{ 'aria-label': t('settings.pinSidebar') }}
            />
          </ListItem>
        )}
        <ListItem className={classes.appearanceItem}>
          <TextField
            fullWidth
            required
            select
            label={t('settings.preferredLanguage')}
            value={preferredLocale}
            onChange={onLocaleChange}
            helperText={t('settings.preferredLanguageHelp')}
          >
            <MenuItem value="en-CA">{t('settings.englishCanada')}</MenuItem>
            <MenuItem value="fr-CA">{t('settings.frenchCanada')}</MenuItem>
          </TextField>
        </ListItem>
      </List>
    </InfoCard>
  );
};

export const ServiceRegistrySettingsPage = () => {
  const { t, locale } = useServiceRegistryTranslation();
  const {
    loading,
    error,
    profile,
    profileComplete,
    isFirstLogin,
    saveProfile,
  } = useServiceRegistryLanguage();
  const alertApi = useApi(alertApiRef);
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState<ServiceRegistryProfileInput>(
    emptyInput(locale),
  );
  const [initialForm, setInitialForm] = useState<ServiceRegistryProfileInput>();
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const bypassPrompt = useRef(false);
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const classes = useStyles();

  useEffect(() => {
    if (!profile) return;
    const value: ServiceRegistryProfileInput = {
      contactPhone: profile.contactPhone,
      jobTitle: profile.jobTitle,
      department: profile.department,
      manager: { ...profile.manager },
      preferredLocale: locale,
    };
    setForm(value);
    setInitialForm(value);
  }, [locale, profile]);

  const dirty = useMemo(
    () =>
      Boolean(
        initialForm && JSON.stringify(form) !== JSON.stringify(initialForm),
      ),
    [form, initialForm],
  );

  useEffect(() => {
    const confirmLeave = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', confirmLeave);
    return () => window.removeEventListener('beforeunload', confirmLeave);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const navigator = navigationContext.navigator as any;
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const originalGo = navigator.go;
    const confirmNavigation = (action: Function, args: unknown[]) => {
      if (
        bypassPrompt.current ||
        // Browser confirmation protects unsaved profile changes.
        // eslint-disable-next-line no-alert
        window.confirm(t('settings.unsavedChanges'))
      ) {
        bypassPrompt.current = false;
        action.apply(navigator, args);
      }
    };
    navigator.push = (...args: unknown[]) =>
      confirmNavigation(originalPush, args);
    navigator.replace = (...args: unknown[]) =>
      confirmNavigation(originalReplace, args);
    navigator.go = (...args: unknown[]) => confirmNavigation(originalGo, args);
    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      navigator.go = originalGo;
    };
  }, [dirty, navigationContext.navigator, t]);

  const update =
    (field: keyof ServiceRegistryProfileInput) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setForm(current => ({ ...current, [field]: event.target.value }));
      if (field === 'contactPhone' || field === 'jobTitle') {
        setErrors(current => ({ ...current, [field]: undefined }));
      }
    };

  const updateManager =
    (field: keyof ServiceRegistryProfileInput['manager']) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setForm(current => ({
        ...current,
        manager: { ...current.manager, [field]: event.target.value },
      }));
      const errorField = `manager${field[0].toUpperCase()}${field.slice(
        1,
      )}` as keyof FieldErrors;
      setErrors(current => ({ ...current, [errorField]: undefined }));
    };

  const requiredError = (value: string, label: string) =>
    value.trim() ? undefined : t('validation.required', { field: label });

  const phoneError = (value: string, label: string) =>
    requiredError(value, label) ||
    (phonePattern.test(value.trim())
      ? undefined
      : t('validation.invalidPhone'));

  const managerEmailError = () => {
    const value = form.manager.email.trim().toLowerCase();
    if (!value) {
      return t('validation.required', { field: t('settings.managerEmail') });
    }
    if (value.length > 100) return t('validation.emailMax');
    if (!emailPattern.test(value)) return t('validation.invalidEmail');
    if (value === profile?.email.trim().toLowerCase()) {
      return t('validation.managerEmailDifferent');
    }
    return undefined;
  };

  const validateField = (field: keyof FieldErrors) => {
    let message: string | undefined;
    if (field === 'contactPhone') {
      message = phoneError(form.contactPhone, t('settings.contactNumber'));
    } else if (field === 'managerPhone') {
      message = phoneError(form.manager.phone, t('settings.managerPhone'));
    } else if (field === 'managerEmail') {
      message = managerEmailError();
    }
    setErrors(current => ({ ...current, [field]: message }));
  };

  const validate = (): FieldErrors => {
    const result: FieldErrors = {};
    const required = (key: keyof FieldErrors, value: string, label: string) => {
      if (!value.trim())
        result[key] = t('validation.required', { field: label });
    };
    result.contactPhone = phoneError(
      form.contactPhone,
      t('settings.contactNumber'),
    );
    required('jobTitle', form.jobTitle, t('settings.jobTitle'));
    required('managerName', form.manager.name, t('settings.managerName'));
    result.managerEmail = managerEmailError();
    result.managerPhone = phoneError(
      form.manager.phone,
      t('settings.managerPhone'),
    );
    required('managerTitle', form.manager.title, t('settings.managerTitle'));

    Object.keys(result).forEach(key => {
      if (!result[key as keyof FieldErrors]) {
        delete result[key as keyof FieldErrors];
      }
    });
    return result;
  };

  const handleCancel = () => {
    if (!initialForm) return;
    setForm(initialForm);
    setErrors({});
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length) return;

    setSaving(true);
    try {
      const clean: ServiceRegistryProfileInput = {
        contactPhone: form.contactPhone.trim(),
        jobTitle: form.jobTitle.trim(),
        department: form.department.trim(),
        manager: {
          name: form.manager.name.trim(),
          email: form.manager.email.trim().toLowerCase(),
          phone: form.manager.phone.trim(),
          title: form.manager.title.trim(),
        },
        preferredLocale: form.preferredLocale,
      };
      await saveProfile(clean);
      setInitialForm(clean);
      alertApi.post({
        message: t('settings.profileSaved'),
        severity: 'success',
      });
      if (!profileComplete) {
        const state = location.state as { returnTo?: string } | null;
        bypassPrompt.current = true;
        navigate(state?.returnTo || '/navcan-home', { replace: true });
      }
    } catch (caught) {
      const code = caught instanceof Error ? caught.message : String(caught);
      if (code === 'MANAGER_EMAIL_MUST_DIFFER_FROM_USER_EMAIL') {
        setErrors({ managerEmail: t('validation.managerEmailDifferent') });
      } else {
        alertApi.post({
          message: t('validation.saveFailed'),
          severity: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Progress />;

  const initials = profile?.fullName
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <Page themeId="tool">
      <Header title={t('settings.title')} />
      <Content>
        <Typography variant="overline">{t('settings.general')}</Typography>
        {(isFirstLogin || !profileComplete) && (
          <Alert severity="warning" style={{ margin: '16px 0' }} role="alert">
            <AlertTitle>{t('settings.completeTitle')}</AlertTitle>
            {t('settings.completeMessage')}
          </Alert>
        )}
        {error && (
          <Typography color="error">{t('errors.bootstrapFailed')}</Typography>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <fieldset disabled={saving} className={classes.fieldset}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <InfoCard title={t('settings.profile')} variant="gridItem">
                  <Grid container spacing={3}>
                    <Grid item>
                      <Avatar style={{ width: 96, height: 96, fontSize: 32 }}>
                        {initials}
                      </Avatar>
                    </Grid>
                    <Grid item xs={12} sm>
                      <Typography variant="h6">{profile?.fullName}</Typography>
                      <Typography color="textSecondary">
                        {profile?.email}
                      </Typography>
                      <Typography color="textSecondary">
                        {t('settings.organization')}: {profile?.organization}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        {t('settings.yourInformation')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label={t('settings.contactNumber')}
                        value={form.contactPhone}
                        onChange={update('contactPhone')}
                        onBlur={() => validateField('contactPhone')}
                        error={Boolean(errors.contactPhone)}
                        helperText={errors.contactPhone}
                        inputProps={{ maxLength: 50 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label={t('settings.jobTitle')}
                        value={form.jobTitle}
                        onChange={update('jobTitle')}
                        error={Boolean(errors.jobTitle)}
                        helperText={errors.jobTitle}
                        inputProps={{ maxLength: 255 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={t('settings.departmentOptional')}
                        value={form.department}
                        onChange={update('department')}
                        inputProps={{ maxLength: 255 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        {t('settings.managerInformation')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label={t('settings.managerName')}
                        value={form.manager.name}
                        onChange={updateManager('name')}
                        error={Boolean(errors.managerName)}
                        helperText={errors.managerName}
                        inputProps={{ maxLength: 255 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        type="email"
                        label={t('settings.managerEmail')}
                        value={form.manager.email}
                        onChange={updateManager('email')}
                        onBlur={() => validateField('managerEmail')}
                        error={Boolean(errors.managerEmail)}
                        helperText={errors.managerEmail}
                        inputProps={{ maxLength: 100 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label={t('settings.managerPhone')}
                        value={form.manager.phone}
                        onChange={updateManager('phone')}
                        onBlur={() => validateField('managerPhone')}
                        error={Boolean(errors.managerPhone)}
                        helperText={errors.managerPhone}
                        inputProps={{ maxLength: 50 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label={t('settings.managerTitle')}
                        value={form.manager.title}
                        onChange={updateManager('title')}
                        error={Boolean(errors.managerTitle)}
                        helperText={errors.managerTitle}
                        inputProps={{ maxLength: 255 }}
                      />
                    </Grid>
                  </Grid>
                </InfoCard>
              </Grid>
              <Grid item xs={12} md={5}>
                <AppearanceCard
                  preferredLocale={form.preferredLocale}
                  onLocaleChange={update('preferredLocale')}
                />
              </Grid>
            </Grid>
          </fieldset>
          <Box className={classes.actions}>
            <Button
              type="button"
              color="secondary"
              variant="text"
              onClick={handleCancel}
              disabled={saving || !dirty}
            >
              {t('common.cancel')}
            </Button>
            <Box className={classes.submitWrapper}>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={saving || !dirty}
              >
                {profileComplete
                  ? t('settings.saveChanges')
                  : t('settings.saveAndContinue')}
              </Button>
              {saving && (
                <CircularProgress
                  size={24}
                  className={classes.buttonProgress}
                />
              )}
            </Box>
          </Box>
        </form>
      </Content>
    </Page>
  );
};
